import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {useDashboardConfigs} from 'app/components/Dashboard/hooks/useDashboardConfigs';
import {MultiFeatureAnalysisWidget} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisWidget';
import {MultiFeatureCategoricalAnalysisWidget} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureCategoricalAnalysisWidget';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import {recordEvent} from 'app/tools/Analytics';
import {useShowInternalToolsOnAltPress} from 'app/utils/userUtils';

import AppNav from '../AppNav';
import Loading from '../Loading';
import {NavSidebarContainer} from '../NavSidebar';
import cs from './Dashboard.module.scss';
import {DEFAULT_DASHBOARD_CONFIG} from './DefaultConfig';
import {FeatureMetadataProvider} from './FeatureMetadataProvider';
import {NoProjectsBlankstate} from './NoProjectsBlankstate';
import {OrgAreaWidget} from './OrgLensStats/OrgAreaWidget';
import {OrgCountsWidget} from './OrgLensStats/OrgCountsWidget';
import {OrgNotesWidget} from './OrgLensStats/OrgNotesWidget';
import {OrgReportsWidget} from './OrgLensStats/OrgReportsWidget';
import {DashboardConfig, WidgetConfig, isMultifeatureAnalysisConfig} from './types';
type DashboardPageProps = {
  loggedInUserActions: LoggedInUserActions;
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
};

const Dashboard: React.FC<DashboardPageProps> = ({loggedInUserActions, organization, profile}) => {
  const [displayedWidgets, setDisplayedWidgets] = React.useState<WidgetConfig[]>([]);
  const [showUnpublishedWidgets, setShowUnpublishedWidgets] = React.useState(false);
  const orgId = organization.get('id');
  const {data: dashboardConfigs, isLoading} = useDashboardConfigs(orgId);

  const activeDashboardConfig: DashboardConfig = React.useMemo(() => {
    if (!dashboardConfigs || Object.keys(dashboardConfigs).length === 0) {
      return DEFAULT_DASHBOARD_CONFIG;
    }
    // For now, we only support one dashboard config per organization.dashboardConfigs
    // Just return the first one in the list since there is only one.
    return Object.values(dashboardConfigs)[0];
  }, [dashboardConfigs]);

  React.useEffect(() => {
    if (!activeDashboardConfig) return;

    const filteredDashboardConfig = activeDashboardConfig.widgets.filter(
      (widget) => widget.isPublished || showUnpublishedWidgets
    );
    setDisplayedWidgets(filteredDashboardConfig);
  }, [activeDashboardConfig, showUnpublishedWidgets]);

  const orgHasOwnConfig = dashboardConfigs && Object.keys(dashboardConfigs).length > 0;

  const [projects, , , projectsMeta] = useProjects();
  const noProjects = !projectsMeta.loading && projects?.size === 0;

  // Log when the dashboard is visited
  React.useEffect(() => {
    recordEvent('Visited dashboard');
  }, []);

  // Widget switch for MFA configs
  const renderMFAWidget = (chart: WidgetConfig) => {
    if (!isMultifeatureAnalysisConfig(chart)) return null;

    const isCategorical =
      chart.statAggregation === 'area_counts' || chart.statAggregation === 'area_percentages';
    if (isCategorical) {
      return (
        <MultiFeatureCategoricalAnalysisWidget widgetConfig={chart} organization={organization} />
      );
    }
    return <MultiFeatureAnalysisWidget widgetConfig={chart} organization={organization} />;
  };

  return (
    <>
      <AppNav
        organization={organization}
        profile={profile}
        selectedProject={null}
        loggedInUserActions={loggedInUserActions}
      />
      <NavSidebarContainer>
        {noProjects ? (
          <NoProjectsBlankstate orgId={organization.get('id')} />
        ) : (
          <div className={cs.dashboardContainer} data-testid="dashboard-container">
            {isLoading ? (
              <Loading />
            ) : (
              <>
                <div className={cs.dashboardHeader}>
                  <h2>Dashboard</h2>
                  <UnpublishedViewToggle
                    profile={profile}
                    showUnpublishedWidgets={showUnpublishedWidgets}
                    setShowUnpublishedWidgets={setShowUnpublishedWidgets}
                  />
                </div>
                <p className={cs.dashboardCTA}>
                  Track monitoring progress and trends across multiple properties. Reach out to{' '}
                  <a href={mailtoUrl}>lens@upstream.tech</a> to explore{' '}
                  <a
                    href="https://lens.tech/support/article/176-lens-dashboard#customized"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => recordEvent('Clicked dashboard support link')}
                  >
                    custom multi-property analysis charts{' '}
                  </a>
                  and {orgHasOwnConfig ? `update your dashboard.` : `pricing.`}
                </p>
                <div className={cs.widgetContainer}>
                  <FeatureMetadataProvider organization={organization}>
                    {({
                      featureMetadata,
                      isFetching,
                      hasNextPage,
                      fetchNextPage,
                      isFetchingNextPage,
                      error,
                      percentLoaded,
                    }) => (
                      <>
                        <OrgCountsWidget
                          featureMetadata={featureMetadata}
                          isFetching={isFetching}
                          hasNextPage={hasNextPage}
                          fetchNextPage={fetchNextPage}
                          isFetchingNextPage={isFetchingNextPage}
                          error={error}
                          percentLoaded={percentLoaded}
                          organization={organization}
                        />
                        <OrgAreaWidget
                          organization={organization}
                          featureMetadata={featureMetadata}
                          isFetching={isFetching}
                          hasNextPage={hasNextPage}
                          fetchNextPage={fetchNextPage}
                          isFetchingNextPage={isFetchingNextPage}
                          error={error}
                          percentLoaded={percentLoaded}
                        />
                        <OrgNotesWidget
                          organization={organization}
                          featureMetadata={featureMetadata}
                        />
                        <OrgReportsWidget
                          organization={organization}
                          featureMetadata={featureMetadata}
                        />
                      </>
                    )}
                  </FeatureMetadataProvider>

                  {displayedWidgets.map(renderMFAWidget)}
                </div>
              </>
            )}
          </div>
        )}
      </NavSidebarContainer>
    </>
  );
};

/**
 * This component is used to toggle the show unpublished widgets checkbox
 * and is only visible for internal users. Some additional state is required
 * to work around compatibiility of blueprintjs and firefox while the alt
 * key is pressed.
 */
const UnpublishedViewToggle: React.FC<{
  profile: I.ImmutableOf<ApiOrganizationUser>;
  showUnpublishedWidgets: boolean;
  setShowUnpublishedWidgets: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({profile, showUnpublishedWidgets, setShowUnpublishedWidgets}) => {
  const allowDebugView = useShowInternalToolsOnAltPress(profile);
  const isFirefox = navigator.userAgent.includes('Firefox');

  const interaction = (
    <B.Switch
      label={`Show unpublished widgets`}
      checked={showUnpublishedWidgets}
      onChange={() => setShowUnpublishedWidgets((prev) => !prev)}
      alignIndicator={B.Alignment.RIGHT}
    />
  );

  return (
    allowDebugView &&
    (isFirefox ? (
      <div onClick={() => setShowUnpublishedWidgets((prev) => !prev)}>{interaction}</div>
    ) : (
      interaction
    ))
  );
};

export default Dashboard;
const subject = encodeURIComponent('Interested in Lens Dashboard customization');
const body = encodeURIComponent(
  "Hello Lens team, \n\nI'd like to learn more about unlocking the Dashboard customization feature in Lens and pricing. \n[Feel free to include any additional details here about the types of charts or analyses you're interested in.] \n\nThanks, \n[Name]"
);
const mailtoUrl = `mailto:lens@upstream.tech?subject=${subject}&body=${body}`;
